import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { IntercomProvider } from 'react-use-intercom';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import {
  CssBaseline,
  ThemeProvider,
  CircularProgress,
  Backdrop,
} from '@material-ui/core';
import { ErrorBoundary } from '@sentry/react';
import store from '@lib/store';
import theme from '@lib/theme';
import { AppCrashNotice, SubtleErrorHandler } from '@molecules';
import { MainLayout } from '@templates';
import { AdminUsers, AlertDetails, AlertLog, Dashboard, LoginForm } from '@pages';

// Commented out - was causing disruptive full-screen error dialogs
// const ErrorThrower = observer(() => {
//   useEffect(() => {
//     if (store.errors.length) {
//       const error = store.errors.shift();
//       if (!error.shown) {
//         throw error;
//       }
//     }
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [store.errors.length]);
//
//   return null;
// });

export default observer(function App() {
  const [loading, setLoading] = useState(true);
  const [lastActiveTime, setLastActiveTime] = useState(Date.now());

  // Monitor user activity
  useEffect(() => {
    const updateActivity = () => {
      setLastActiveTime(Date.now());
    };

    // Track user activity
    window.addEventListener('mousemove', updateActivity);
    window.addEventListener('keypress', updateActivity);
    window.addEventListener('click', updateActivity);
    window.addEventListener('scroll', updateActivity);

    // Check for idle timeout and reconnect if needed
    const idleCheckInterval = setInterval(() => {
      const idleTime = Date.now() - lastActiveTime;
      const isIdle = idleTime > 60000; // 1 minute of inactivity
      
      if (!isIdle && store.user && !store.userCableReady) {
        store.reconnectCable();
      }
    }, 10000); // Check every 10 seconds

    // Monitor online/offline status
    const handleOnline = () => {
      if (store.user && !store.userCableReady) {
        setTimeout(() => store.reconnectCable(), 1000);
      }
    };

    const handleOffline = () => {
      // Network connection lost
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Monitor page visibility
    const handleVisibilityChange = () => {
      if (!document.hidden && store.user && !store.userCableReady) {
        setTimeout(() => store.reconnectCable(), 500);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('mousemove', updateActivity);
      window.removeEventListener('keypress', updateActivity);
      window.removeEventListener('click', updateActivity);
      window.removeEventListener('scroll', updateActivity);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(idleCheckInterval);
    };
  }, [lastActiveTime]);

  useEffect(() => {
    if (!store.performedInitialUserFetch) {
      store.loadCurrentUser().finally(() => {
        setLoading(false);
      });
      return;
    }

    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store.performedInitialUserFetch]);

  if (loading) {
    return (
      <Backdrop open>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  return (
    <IntercomProvider appId={process.env.INTERCOM_APP_ID}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <BrowserRouter>
          {/* 
          COMMENTED OUT: Disruptive full-screen error dialog that blocks medical monitoring UI
          <ErrorBoundary
            showDialog
            fallback={({ error, resetError }) => (
              <ErrorDialog error={error} resetError={resetError} />
            )}
          >
          */}
          {/*
            The boundary wraps the router, so anything it catches has already
            taken the whole dashboard down. The fallback therefore has to SAY
            monitoring stopped — it used to render the small "App continues
            running" toast over a blank page, which was the opposite of true.
          */}
          <ErrorBoundary
            fallback={({ error, resetError }) => (
              <AppCrashNotice error={error as Error} resetError={resetError} />
            )}
          >
            {/* 
            COMMENTED OUT: ErrorThrower that forces errors to bubble up and block UI
            <ErrorThrower />
            */}
            {/* Subtle error handler for store errors - non-blocking */}
            <SubtleErrorHandler />
            <Routes>
              {!store.user ? (
                <Route path="*" element={<LoginForm />} />
              ) : (
                <Route element={<MainLayout />}>
                  {store.user.admin && (
                    <>
                      {/*
                        Admin-only, like the alert log beside it. This route was
                        open to everyone and relied on a client-side password
                        check inside the page to turn people away — a guard that
                        no longer exists, since the API now decides.
                      */}
                      <Route path="/admin/users" element={<AdminUsers />} />
                      <Route path="/alert-log/:id" element={<AlertDetails />} />
                      <Route path="/alert-log" element={<AlertLog />} />
                    </>
                  )}
                  <Route path="/alerts/:id" element={<Dashboard />} />
                  <Route path="*" element={<Dashboard />} />
                </Route>
              )}
            </Routes>
          </ErrorBoundary>
        </BrowserRouter>
      </ThemeProvider>
    </IntercomProvider>
  );
});
